import { useEffect, useRef, useState } from "react";
import { Pause, Play, UploadCloud, WifiOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StripedBar } from "@/components/app/ProcessingProgress";
import { useApp } from "@/lib/i18n";

const mb = (b) => (b / 1048576).toFixed(1);
const fmtEta = (s) => (s >= 3600 ? `${Math.floor(s / 3600)}j ${Math.round((s % 3600) / 60)}m` : s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);

// Rolling ~8s window throughput in bytes/sec.
const useSpeed = (loaded, active) => {
  const samples = useRef([]);
  const [speed, setSpeed] = useState(0);
  useEffect(() => {
    const now = Date.now();
    if (!active) { samples.current = []; setSpeed(0); return; }
    samples.current = [...samples.current.filter((x) => now - x[0] < 8000), [now, loaded]];
    const [t0, l0] = samples.current[0];
    if (now - t0 > 1500) setSpeed(Math.max(0, (loaded - l0) / ((now - t0) / 1000)));
  }, [loaded, active]);
  return speed;
};

export const UploadProgress = ({ status, loaded, total, resumedFrom, filename, compact, compressPct, originalSize, onPause, onResume, onCancel }) => {
  const { L } = useApp();
  const pct = status === "compressing" ? compressPct || 0 : total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
  const TITLE = {
    preparing: L("Menyediakan muat naik…", "Preparing upload…"),
    compressing: L("Memampatkan audio untuk muat naik lebih pantas…", "Compressing audio for a faster upload…"),
    uploading: L("Memuat naik rakaman…", "Uploading recording…"),
    resuming: L(`Menyambung dari ${Math.round((resumedFrom / total) * 100)}%…`, `Resuming from ${Math.round((resumedFrom / total) * 100)}%…`),
    paused: L("Dijeda", "Paused"),
    offline: L("Tiada internet — akan sambung automatik", "Offline — will resume automatically"),
    retrying: L("Sambungan terganggu — mencuba semula…", "Connection issue — retrying…"),
    finalizing: L("Mengesahkan fail di pelayan…", "Validating file on server…"),
  };
  const speed = useSpeed(loaded, status === "uploading");
  const canControl = !["preparing", "finalizing"].includes(status);
  const Icon = status === "offline" ? WifiOff : status === "paused" ? Pause : UploadCloud;
  return (
    <div className={`fixed bottom-0 right-0 z-50 p-3 sm:p-5 ${compact ? "w-full sm:w-[440px]" : "left-0 lg:left-64"}`} data-testid="upload-progress" data-compact={compact ? "true" : "false"} data-status={status}>
      <div className={`mx-auto max-w-2xl rounded-3xl border-2 bg-white shadow-2xl ${status === "offline" || status === "retrying" ? "border-amber-300 shadow-amber-100" : "border-violet-300 shadow-violet-200"} ${compact ? "p-4" : "p-5 sm:p-6"}`}>
        <div className="flex items-center gap-4">
          <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white ${status === "paused" ? "bg-slate-400" : status === "offline" || status === "retrying" ? "bg-amber-500" : "animate-pulse bg-violet-600"}`}><Icon size={24} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-slate-900" data-testid="upload-status-text">{TITLE[status]}</p>
            <p className="truncate text-xs text-slate-500" data-testid="upload-bytes">{filename && `${filename} · `}{status === "compressing" ? `${mb(total)} MB` : `${mb(loaded)} / ${mb(total)} MB`}{originalSize && status !== "compressing" ? ` (${L("asal", "was")} ${mb(originalSize)} MB)` : ""}</p>
            {speed > 0 && <p className="text-xs font-medium text-violet-700" data-testid="upload-speed">{mb(speed)} MB/s · {L("baki", "left")} ~{fmtEta((total - loaded) / speed)}</p>}
          </div>
          <span className="font-mono text-3xl font-extrabold text-violet-700" data-testid="upload-percent">{pct}%</span>
        </div>
        <div className="mt-4"><StripedBar value={status === "finalizing" || status === "preparing" ? null : pct} testId="upload-bar" /></div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-amber-700">{L("Boleh buka halaman lain. Jika tab tertutup, pilih fail yang sama untuk sambung.", "You can browse other pages. If the tab closes, pick the same file to resume.")}</p>
          {canControl && (
            <div className="flex gap-2">
              {status === "compressing" ? null : status === "paused"
                ? <Button size="sm" className="h-8 rounded-xl bg-violet-600 hover:bg-violet-700" onClick={onResume} data-testid="upload-resume-btn"><Play size={14} className="mr-1" />{L("Sambung", "Resume")}</Button>
                : <Button size="sm" variant="outline" className="h-8 rounded-xl" onClick={onPause} data-testid="upload-pause-btn"><Pause size={14} className="mr-1" />{L("Jeda", "Pause")}</Button>}
              <Button size="sm" variant="ghost" className="h-8 rounded-xl text-red-600" onClick={onCancel} data-testid="upload-cancel-btn"><X size={14} className="mr-1" />{L("Batal", "Cancel")}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
